/**
 * Pure DOM copy-settings transforms (OpenSpec `monorepo-kit-editor` tasks 5.5 /
 * 8.2). Moved verbatim from `src/helpers/copyHelpers/copyHelpers-v2.ts`, whose
 * census disposition is `split`: this half is sync, DOM-only, and IO-free, so
 * it belongs in the kernel; the clipboard writes, retries, analytics and
 * Safari/Tauri branches stay app-side.
 *
 * Both variants are here because they share the same private machinery. The
 * "legacy editor" variant is not legacy *pipeline* code — it is the same pure
 * DOM transform with one extra normalization for Heidi's historical
 * `<p>- text</p>` bullets, and splitting it out would either duplicate ~250
 * lines or force a crossing into package internals.
 */
import { type CopySettings } from './copy-settings';

const unwrapElement = (el: Element): void => {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
};

/**
 * Replace headings (`<h1>` … `<h6>`) with `<p>` elements. We keep the
 * children so the text survives — only the heading semantics (and the
 * UA-default bold/large styling that paste targets like MS Word respect)
 * are dropped.
 */
const replaceHeadingsWithParagraphs = (root: Element): void => {
  const headings = root.querySelectorAll('h1, h2, h3, h4, h5, h6');
  headings.forEach((heading) => {
    const ownerDoc = heading.ownerDocument;
    if (!ownerDoc) return;
    const p = ownerDoc.createElement('p');
    while (heading.firstChild) {
      p.appendChild(heading.firstChild);
    }
    heading.parentNode?.replaceChild(p, heading);
  });
};

/**
 * Unwrap inline emphasis tags. Mirrors `removeAnnotationFromMarkdown`'s
 * behaviour on the HTML side so the markdown sibling produced by
 * `simpleHtmlToMarkdown` doesn't drift out of sync.
 */
const stripBoldAndItalics = (root: Element): void => {
  const targets = root.querySelectorAll('strong, b, em, i, u');
  targets.forEach(unwrapElement);
};

const BLOCK_ELEMENT_TAGS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'details',
  'dialog',
  'div',
  'dl',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hgroup',
  'hr',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'ul',
]);

const isBlockElement = (node: ChildNode): node is Element =>
  node.nodeType === 1 && BLOCK_ELEMENT_TAGS.has((node as Element).tagName.toLowerCase());

/**
 * Flatten every semantic list bottom-up. Processing descendants before their
 * ancestors means a list nested anywhere inside a list item (including inside
 * a blockquote, table, or other block) is already replaced when its outer list
 * is visited. Existing block nodes are promoted intact; only adjacent inline
 * content is wrapped in a paragraph, so we never create invalid `<p><pre>` or
 * `<p><blockquote>` structures.
 */
const flattenLists = (root: Element): void => {
  const lists = Array.from(root.querySelectorAll('ul, ol')).reverse();

  lists.forEach((list) => {
    const ownerDoc = list.ownerDocument;
    const parent = list.parentNode;
    if (!ownerDoc || !parent) return;

    const flattened = ownerDoc.createDocumentFragment();
    const items = Array.from(list.children).filter(
      (child) => child.tagName.toLowerCase() === 'li'
    );

    items.forEach((li) => {
      let inlineParagraph = ownerDoc.createElement('p');
      const flushInlineParagraph = (): void => {
        const hasContent =
          inlineParagraph.childElementCount > 0 ||
          /\S/.test(inlineParagraph.textContent ?? '');
        if (hasContent) flattened.appendChild(inlineParagraph);
        inlineParagraph = ownerDoc.createElement('p');
      };

      Array.from(li.childNodes).forEach((child) => {
        if (isBlockElement(child)) {
          flushInlineParagraph();
          flattened.appendChild(child);
          return;
        }
        inlineParagraph.appendChild(child);
      });
      flushInlineParagraph();
    });

    parent.insertBefore(flattened, list);
    parent.removeChild(list);
  });
};

/**
 * Legacy Heidi editors can store bullets as ordinary paragraphs
 * (`<p>- text</p>`). Remove that marker while the source structure is available;
 * code-like descendants, table cells, and semantic-list contents remain literal.
 * Numeric prose such as `2024. Last colonoscopy` is deliberately untouched.
 */
const stripLiteralBulletParagraphMarkers = (root: Element): void => {
  const firstMeaningfulNode = (element: Element): ChildNode | null => {
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === 3 && /\S/.test(child.textContent ?? '')) return child;
      if (child.nodeType === 1) {
        const childElement = child as Element;
        if (
          childElement.matches('code, pre, kbd, samp, img, svg, math, br') &&
          (childElement.matches('img, svg, math, br') ||
            /\S/.test(childElement.textContent ?? ''))
        ) {
          return child;
        }
        const nested = firstMeaningfulNode(childElement);
        if (nested) return nested;
      }
    }
    return null;
  };

  root.querySelectorAll('p').forEach((paragraph) => {
    if (paragraph.closest('table, pre, li, ul, ol')) return;
    const firstNode = firstMeaningfulNode(paragraph);
    if (firstNode?.nodeType !== 3 || firstNode.textContent == null) return;
    firstNode.textContent = firstNode.textContent.replace(/^(\s*)-\s+/, '$1');
  });
};

/**
 * Lift a cell's content to inline markup for splicing into a row paragraph.
 *
 * Both editor schemas declare table cells `block+`, so a real cell is
 * `<td><p>text</p></td>`, never `<td>text</td>`. Splicing that raw would nest
 * `<p>` inside `<p>` — markup the next HTML parse splits back into siblings,
 * which is how a flattened table degraded into one cell per line, with every
 * empty cell surfacing as an empty paragraph (the markdown-native blank-line
 * marker, pasted as a literal `&nbsp;`).
 *
 * Inline marks are kept, so the toggles stay orthogonal. Blocks that sit side
 * by side within one cell are joined with a space rather than a line break:
 * a row has to stay a single paragraph for the tab delimiters to mean
 * anything. List markers drop here for the same reason `flattenLists` drops
 * them — a `<ul>` cannot survive inside a `<p>` either.
 */
const cellInlineHtml = (cell: Element, ownerDoc: Document): string => {
  const holder = ownerDoc.createElement('div');

  const appendInline = (source: Node): void => {
    Array.from(source.childNodes).forEach((child) => {
      const isListItem =
        child.nodeType === 1 && (child as Element).tagName.toLowerCase() === 'li';
      if (isBlockElement(child) || isListItem) {
        // Anchored at the end so nested wrappers (`<li>` around `<p>`) each
        // recursing here still contribute a single separator between siblings.
        if (/\S$/.test(holder.textContent ?? '')) {
          holder.appendChild(ownerDoc.createTextNode(' '));
        }
        appendInline(child);
        return;
      }
      holder.appendChild(child.cloneNode(true));
    });
  };

  appendInline(cell);
  return holder.innerHTML.trim();
};

/**
 * Flatten `<table>` to plain `<p>` rows separated by tabs. Mirrors what
 * Excel/Word do when you paste a tab-delimited string back into them —
 * the destination still gets row/column structure, just without the
 * table chrome.
 *
 * Three correctness details:
 *
 * 1. **Leaves first.** `:has(table)` selects tables that contain another
 *    table, so the complement (`table:not(:has(table))`) is the set of
 *    innermost tables. We flatten those, then loop — the next iteration
 *    sees the parent table now-childless and treats it as a leaf. This
 *    avoids the double-emit bug where querying `<tr>` on an outer table
 *    matched inner rows too (and the inner cells' `textContent` already
 *    contained the inner table's text via DOM recursion).
 *
 * 2. **Inline markup, not raw `innerHTML`.** `cellInlineHtml` preserves inline
 *    marks (`<strong>`, `<em>`, …) so a user on `tables: false,
 *    boldAndItalics: true` keeps their bold, while unwrapping the cell's block
 *    children so the row paragraph stays well-formed. The downstream
 *    `stripBoldAndItalics` pass handles the marks when both toggles are off.
 *
 * 3. **A row of only empty cells emits nothing.** Its paragraph would hold
 *    just tabs — whitespace the markdown serializer reads back as an empty
 *    paragraph (blank-line marker again) or, worse, as indented code.
 */
const flattenTables = (root: Element): void => {
  // `:has(table)` may not be supported in very old jsdom; fall back to a
  // recursion check if needed. Modern jsdom (≥21) + every browser we ship
  // to support it.
  const findLeafTable = (): HTMLTableElement | null => {
    const tables = root.querySelectorAll('table');
    for (const t of Array.from(tables)) {
      if (t.querySelector('table') == null) return t;
    }
    return null;
  };

  let table = findLeafTable();
  while (table != null) {
    const ownerDoc = table.ownerDocument;
    const parent = table.parentNode;
    if (!ownerDoc || !parent) {
      table.remove();
      table = findLeafTable();
      continue;
    }
    // Scope row query to this table only — since `table` is now a leaf
    // (no nested table) every `<tr>` it contains is its own.
    const rows = Array.from(table.querySelectorAll('tr'));
    rows.forEach((tr) => {
      const cells = Array.from(tr.querySelectorAll('th, td'));
      const cellHtml = cells.map((cell) => cellInlineHtml(cell, ownerDoc));
      if (cellHtml.every((cell) => cell === '')) return;

      const p = ownerDoc.createElement('p');
      p.innerHTML = cellHtml.join('\t');
      parent.insertBefore(p, table);
    });
    parent.removeChild(table);
    table = findLeafTable();
  }
};

type CopySettingsHtmlOptions = {
  stripLegacyLiteralBulletParagraphs: boolean;
};

/**
 * Apply `CopySettings` to an HTML payload. Each toggle is orthogonal: `false`
 * strips the corresponding element class; `true` leaves it intact.
 *
 * - `headings: false` → unwrap `<h1>`–`<h6>` into `<p>`
 * - `boldAndItalics: false` → unwrap `<strong>` / `<b>` / `<em>` / `<i>` / `<u>`
 * - `bulletPoints: false` → flatten `<ul>` / `<ol>` into a sequence of `<p>`
 * - `tables: false` → flatten `<table>` rows into tab-delimited `<p>`
 *
 * Literal `<p>- text</p>` cleanup is source-specific. Legacy editors can store
 * bullets that way, whereas canonical markdown uses the same HTML for an
 * escaped, literal dash. Callers must therefore opt into that legacy pass.
 */
const applyCopySettingsToHtmlInternal = (
  html: string,
  settings: CopySettings,
  options: CopySettingsHtmlOptions
): string => {
  if (!html) return html;
  // jsdom in tests and every modern browser ship DOMParser. The function
  // is sync-only — no fetch, no IO.
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html');
  const root = doc.body;

  // Strip legacy paragraph markers while table/list ancestry can distinguish
  // them from literal content, then flatten semantic lists bottom-up.
  if (!settings.bulletPoints) {
    if (options.stripLegacyLiteralBulletParagraphs) {
      stripLiteralBulletParagraphMarkers(root);
    }
    flattenLists(root);
  }
  // Keep table cell inline marks until the explicit bold/italics pass below.
  if (!settings.tables) {
    flattenTables(root);
  }
  if (!settings.headings) {
    replaceHeadingsWithParagraphs(root);
  }
  if (!settings.boldAndItalics) {
    stripBoldAndItalics(root);
  }

  return root.innerHTML;
};

/**
 * Semantic HTML transform used by canonical markdown copy/export and by direct
 * callers. A literal dash paragraph remains literal; only `<ul>` / `<ol>`
 * structure is treated as a list.
 */
export const applyCopySettingsToHtml = (html: string, settings: CopySettings): string =>
  applyCopySettingsToHtmlInternal(html, settings, {
    stripLegacyLiteralBulletParagraphs: false,
  });

/**
 * Legacy editor transform. In addition to semantic lists, Heidi's historical
 * `<p>- text</p>` bullet representation is normalized when bullets are off.
 */
export const applyCopySettingsToLegacyEditorHtml = (
  html: string,
  settings: CopySettings
): string =>
  applyCopySettingsToHtmlInternal(html, settings, {
    stripLegacyLiteralBulletParagraphs: true,
  });

/**
 * True when every formatting toggle — headings, bold/italics, tables — is
 * off, bullets ignored: `- ` markers survive a plain paste, so only a
 * formatting toggle justifies a `text/html` flavor.
 */
export const isFormattingOff = (settings: CopySettings): boolean =>
  !settings.headings && !settings.boldAndItalics && !settings.tables;
